//@ts-nocheck
import { useEffect, useMemo, useState, useLocation } from "react";
import { Table, Button, Input, Select, message } from "antd";
import { PlusOutlined, SearchOutlined, LeftOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import AddSourceModal from "./addSource";
import { LiaEditSolid } from "react-icons/lia";
import { RiDeleteBinLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import {
  createSource,
  deleteSource,
  getSources,
  updateSource,
  type SourceResponse,
} from "../../utils/API";
import NavigationDropdown from "../../Components/NavigationDropDown";
import PageLogoutButton from "../../Components/PageLogoutButton";

interface SourceData extends SourceResponse {
  key: string;
}

const SourceMaster = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [sources, setSources] = useState<SourceData[]>([]);
  const [editingSource, setEditingSource] = useState<SourceData | null>(null);

  const fetchSources = async () => {
    try {
      setLoading(true);
      const response = await getSources();
      const mappedData = response.map((item) => ({
        ...item,
        key: String(item.id),
      }));
      setSources(mappedData);
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to fetch sources");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSources();
  }, []);

  const filteredSources = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    if (!search) return sources;

    return sources.filter(
      (item) =>
        item.sourceName?.toLowerCase().includes(search) ||
        item.slug?.toLowerCase().includes(search)
    );
  }, [sources, searchText]);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingSource(null);
  };

  const handleSubmitSource = async (sourceName: string) => {
    try {
      setSubmitLoading(true);
      if (editingSource) {
        await updateSource(editingSource.id, { sourceName: sourceName.trim() });
        // message.success("Source updated successfully");
      } else {
        await createSource({ sourceName: sourceName.trim() });
        // message.success("Source added successfully");
      }

      handleModalClose();
      await fetchSources();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to save source");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteSource = async (record: SourceData) => {
    const isConfirmed = window.confirm(
      `Are you sure you want to delete "${record.sourceName}"?`
    );
    if (!isConfirmed) return;

    try {
      await deleteSource(record.id);
      // message.success("Source deleted successfully");
      await fetchSources();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to delete source");
    }
  };

  const columns: ColumnsType<SourceData> = [
    {
      title: "#",
      key: "index",
      align: "left",
      width: "20%",
      render: (_, __, index) => index + 1,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          color: "black",
          fontWeight: "600",
          paddingLeft: "40px",
          fontSize: "14px",
        },
      }),
      onCell: () => ({
        style: { paddingLeft: "40px", fontWeight: "400" },
      }),
    },
    {
      title: "Source Name",
      dataIndex: "sourceName",
      key: "sourceName",
      align: "left",
      width: "35%",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          color: "black",
          fontWeight: "600",
          fontSize: "14px",
        },
      }),
      onCell: () => ({
        style: { fontWeight: "400" },
      }),
    },
    {
      title: "Source Slug",
      dataIndex: "slug",
      key: "slug",
      align: "left",
      width: "35%",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          color: "black",
          fontWeight: "600",
          fontSize: "14px",
        },
      }),
      onCell: () => ({
        style: { fontWeight: "400" },
      }),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: "10%",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          color: "black",
          fontWeight: "600",
          paddingRight: "40px",
          fontSize: "14px",
        },
      }),
      onCell: () => ({
        style: { paddingRight: "40px" },
      }),
      render: (_, record) => (
        <div className="flex justify-center gap-4">
          <LiaEditSolid
            className="text-gray-800 cursor-pointer hover:text-blue-600 text-lg"
            onClick={() => {
              setEditingSource(record);
              setIsModalOpen(true);
            }}
          />
          <RiDeleteBinLine
            className="text-gray-600 cursor-pointer hover:text-red-600 text-lg"
            onClick={() => handleDeleteSource(record)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="p-2 bg-slate-50 min-h-screen">
      <div className="flex justify-end mb-2">
        <PageLogoutButton />
      </div>
      <div className="flex justify-between items-center p-2 mb-4 gap-2">
        <div className="flex items-center gap-4">
          <Button
            icon={<LeftOutlined />}
            size="small"
            className="flex items-center justify-center border-gray-300"
            onClick={() => navigate("/ate-dashboard")}
          />

          <NavigationDropdown />

          <h1 className="text-base sm:text-xl font-normal text-gray-700">
            Source
          </h1>
        </div>
        <div className="flex flex-1 justify-end gap-4 items-center ">
          <Input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchOutlined className="text-xl" />}
            placeholder="Search ...."
            className="rounded-lg h-10 border-gray-300 flex-1 max-w-xs"
          />
          <Button
            type="primary"
            className="bg-[#004aad] hover:bg-blue-800 rounded-lg border-none flex items-center justify-center font-light flex-shrink-0 text-white h-8 w-8 sm:h-10 sm:w-auto sm:px-6"
            onClick={() => {
              setEditingSource(null);
              setIsModalOpen(true);
            }}
          >
            <PlusOutlined className="text-base sm:text-lg" />
            <span className="!hidden sm:!inline ml-2">Add</span>
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-[24px] border border-gray-200 p-6 shadow-sm">
        <div className="rounded-xl overflow-hidden mx-3">
          <Table
            dataSource={filteredSources}
            columns={columns}
            pagination={false}
            loading={loading}
            className="custom-table"
            rowClassName={() => "h-16"}
          />
        </div>
      </div>

      <AddSourceModal
        open={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleSubmitSource}
        loading={submitLoading}
        initialSourceName={editingSource?.sourceName || ""}
        mode={editingSource ? "edit" : "add"}
      />
    </div>
  );
};

export default SourceMaster;
